<div>
  <!-- Page Header -->
  <div class="row mb-4">
    <div class="col">
      <h1 class="h2 mb-1 fw-bold">Reset Integration Data</h1>
      <p class="text-muted">Reset test/development data from external integrations</p>
    </div>
  </div>

  <!-- Environment Warning Banner -->
  <div *ngIf="isProduction" class="alert alert-danger d-flex align-items-center mb-4" role="alert">
    <i class="bi bi-exclamation-triangle-fill me-3 fs-4"></i>
    <div>
      <strong>Production Environment</strong>
      <p class="mb-0">You are connected to the PRODUCTION environment. Data deleted here cannot be recovered. Proceed with extreme caution.</p>
    </div>
  </div>

  <div *ngIf="!isProduction" class="alert alert-warning d-flex align-items-center mb-4" role="alert">
    <i class="bi bi-info-circle-fill me-3 fs-4"></i>
    <div>
      <strong>Staging Environment</strong>
      <p class="mb-0">This action will permanently delete data from Zoho FSM, Zoho Desk, and Firebase Auth. Protected seed data will be preserved.</p>
    </div>
  </div>

  <!-- Wizard Progress Indicator -->
  <div class="card mb-4" *ngIf="wizardState !== 'results'">
    <div class="card-body py-3">
      <div class="d-flex justify-content-between align-items-center">
        <div class="step d-flex flex-column align-items-center gap-2" [class.active]="wizardState === 'selection'" [class.completed]="wizardState !== 'selection'">
          <span class="step-number rounded-circle d-flex align-items-center justify-content-center fw-semibold small">1</span>
          <span class="step-label text-center text-nowrap">Select Categories</span>
        </div>
        <div class="step-connector flex-grow-1 mx-2 align-self-start"></div>
        <div class="step d-flex flex-column align-items-center gap-2" [class.active]="wizardState === 'preview'" [class.completed]="wizardState === 'confirm' || wizardState === 'progress'">
          <span class="step-number rounded-circle d-flex align-items-center justify-content-center fw-semibold small">2</span>
          <span class="step-label text-center text-nowrap">Preview</span>
        </div>
        <div class="step-connector flex-grow-1 mx-2 align-self-start"></div>
        <div class="step d-flex flex-column align-items-center gap-2" [class.active]="wizardState === 'confirm'" [class.completed]="wizardState === 'progress'">
          <span class="step-number rounded-circle d-flex align-items-center justify-content-center fw-semibold small">3</span>
          <span class="step-label text-center text-nowrap">Confirm</span>
        </div>
        <div class="step-connector flex-grow-1 mx-2 align-self-start"></div>
        <div class="step d-flex flex-column align-items-center gap-2" [class.active]="wizardState === 'progress'">
          <span class="step-number rounded-circle d-flex align-items-center justify-content-center fw-semibold small">4</span>
          <span class="step-label text-center text-nowrap">Execute</span>
        </div>
      </div>
    </div>
  </div>

  <!-- STEP 1: Category Selection -->
  <div *ngIf="wizardState === 'selection'">
    <div class="row">
      <!-- Zoho FSM Section -->
      <div class="col-lg-4 mb-4">
        <div class="card h-100">
          <div class="card-header d-flex justify-content-between align-items-center">
            <div>
              <i class="bi bi-cloud me-2"></i>
              <strong>Zoho FSM</strong>
            </div>
            <div class="btn-group btn-group-sm">
              <button type="button" class="btn btn-outline-secondary" (click)="selectAllInSection('zohoFsm')" [disabled]="isAllSelectedInSection('zohoFsm')">
                All
              </button>
              <button type="button" class="btn btn-outline-secondary" (click)="deselectAllInSection('zohoFsm')" [disabled]="!hasSelectionInSection('zohoFsm')">
                None
              </button>
            </div>
          </div>
          <div class="card-body">
            <div class="form-check mb-3">
              <input class="form-check-input" type="checkbox" id="fsm-drones"
                     [checked]="categories.zohoFsm.drones"
                     (change)="toggleCategory('zohoFsm', 'drones')">
              <label class="form-check-label cursor-pointer" for="fsm-drones">
                <strong>Drones</strong>
                <small class="d-block text-muted">Delete Assets and Products from FSM</small>
              </label>
            </div>
            <div class="form-check mb-3">
              <input class="form-check-input" type="checkbox" id="fsm-service-appointments"
                     [checked]="categories.zohoFsm.serviceAppointments"
                     (change)="toggleCategory('zohoFsm', 'serviceAppointments')">
              <label class="form-check-label cursor-pointer" for="fsm-service-appointments">
                <strong>Service Appointments</strong>
                <small class="d-block text-muted">Delete Requests, Appointments, and Work Orders</small>
              </label>
            </div>
            <div class="form-check mb-3">
              <input class="form-check-input" type="checkbox" id="fsm-work-orders"
                     [checked]="categories.zohoFsm.workOrders"
                     (change)="toggleCategory('zohoFsm', 'workOrders')">
              <label class="form-check-label cursor-pointer" for="fsm-work-orders">
                <strong>Work Orders</strong>
                <small class="d-block text-muted">Delete Work Orders from FSM</small>
              </label>
            </div>
            <div class="form-check mb-3">
              <input class="form-check-input" type="checkbox" id="fsm-organizations"
                     [checked]="categories.zohoFsm.organizations"
                     (change)="toggleCategory('zohoFsm', 'organizations')">
              <label class="form-check-label cursor-pointer" for="fsm-organizations">
                <strong>Organizations</strong>
                <small class="d-block text-muted">Delete Companies from FSM</small>
              </label>
            </div>
            <div class="form-check">
              <input class="form-check-input" type="checkbox" id="fsm-contacts"
                     [checked]="categories.zohoFsm.contacts"
                     (change)="toggleCategory('zohoFsm', 'contacts')">
              <label class="form-check-label cursor-pointer" for="fsm-contacts">
                <strong>Contacts</strong>
                <small class="d-block text-muted">Delete Contacts from FSM</small>
              </label>
            </div>
          </div>
        </div>
      </div>

      <!-- Zoho Desk Section -->
      <div class="col-lg-4 mb-4">
        <div class="card h-100">
          <div class="card-header d-flex justify-content-between align-items-center">
            <div>
              <i class="bi bi-headset me-2"></i>
              <strong>Zoho Desk</strong>
            </div>
            <div class="btn-group btn-group-sm">
              <button type="button" class="btn btn-outline-secondary" (click)="selectAllInSection('zohoDesk')" [disabled]="isAllSelectedInSection('zohoDesk')">
                All
              </button>
              <button type="button" class="btn btn-outline-secondary" (click)="deselectAllInSection('zohoDesk')" [disabled]="!hasSelectionInSection('zohoDesk')">
                None
              </button>
            </div>
          </div>
          <div class="card-body">
            <div class="form-check mb-3">
              <input class="form-check-input" type="checkbox" id="desk-support-tickets"
                     [checked]="categories.zohoDesk.supportTickets"
                     (change)="toggleCategory('zohoDesk', 'supportTickets')">
              <label class="form-check-label cursor-pointer" for="desk-support-tickets">
                <strong>Support Tickets</strong>
                <small class="d-block text-muted">Delete Tickets (includes threads and attachments)</small>
              </label>
            </div>
            <div class="form-check mb-3">
              <input class="form-check-input" type="checkbox" id="desk-organizations"
                     [checked]="categories.zohoDesk.organizations"
                     (change)="toggleCategory('zohoDesk', 'organizations')">
              <label class="form-check-label cursor-pointer" for="desk-organizations">
                <strong>Organizations</strong>
                <small class="d-block text-muted">Delete Accounts from Desk</small>
              </label>
            </div>
            <div class="form-check mb-3">
              <input class="form-check-input" type="checkbox" id="desk-contacts"
                     [checked]="categories.zohoDesk.contacts"
                     (change)="toggleCategory('zohoDesk', 'contacts')">
              <label class="form-check-label cursor-pointer" for="desk-contacts">
                <strong>Contacts</strong>
                <small class="d-block text-muted">Delete Contacts from Desk</small>
              </label>
            </div>
            <div class="form-check">
              <input class="form-check-input" type="checkbox" id="desk-drones"
                     [checked]="categories.zohoDesk.drones"
                     (change)="toggleCategory('zohoDesk', 'drones')">
              <label class="form-check-label cursor-pointer" for="desk-drones">
                <strong>Drones</strong>
                <small class="d-block text-muted">Delete Products from Desk</small>
              </label>
            </div>
          </div>
        </div>
      </div>

      <!-- Firebase Section -->
      <div class="col-lg-4 mb-4">
        <div class="card h-100">
          <div class="card-header d-flex justify-content-between align-items-center">
            <div>
              <i class="bi bi-person-badge me-2"></i>
              <strong>Firebase Auth</strong>
            </div>
            <div class="btn-group btn-group-sm">
              <button type="button" class="btn btn-outline-secondary" (click)="selectAllInSection('firebase')" [disabled]="isAllSelectedInSection('firebase')">
                All
              </button>
              <button type="button" class="btn btn-outline-secondary" (click)="deselectAllInSection('firebase')" [disabled]="!hasSelectionInSection('firebase')">
                None
              </button>
            </div>
          </div>
          <div class="card-body">
            <div class="form-check">
              <input class="form-check-input" type="checkbox" id="firebase-users"
                     [checked]="categories.firebase.users"
                     (change)="toggleCategory('firebase', 'users')">
              <label class="form-check-label cursor-pointer" for="firebase-users">
                <strong>Users</strong>
                <small class="d-block text-muted">Delete Firebase Auth users (except super admins)</small>
              </label>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Preview Error -->
    <div *ngIf="previewError" class="alert alert-danger mb-4" role="alert">
      <i class="bi bi-exclamation-triangle-fill me-2"></i>
      {{ previewError }}
    </div>

    <!-- Action Buttons -->
    <div class="d-flex justify-content-between">
      <button type="button" class="btn btn-outline-secondary" (click)="cancel()">
        Cancel
      </button>
      <button type="button" class="btn btn-primary"
              [disabled]="!hasAnySelection() || previewLoading"
              (click)="goToPreview()">
        <span *ngIf="previewLoading" class="spinner-border spinner-border-sm me-2" role="status"></span>
        <span *ngIf="!previewLoading">Preview Reset ({{ getSelectedCount() }} selected)</span>
        <span *ngIf="previewLoading">Loading preview...</span>
      </button>
    </div>
  </div>

  <!-- STEP 2: Preview Display -->
  <div *ngIf="wizardState === 'preview' && previewData">
    <div class="card mb-4">
      <div class="card-header">
        <h5 class="mb-0">Reset Preview Summary</h5>
      </div>
      <div class="card-body">
        <!-- Summary Table -->
        <div class="table-responsive mb-4">
          <table class="table table-bordered">
            <thead>
              <tr>
                <th>Category</th>
                <th class="text-center">Total Records</th>
                <th class="text-center">To Delete</th>
                <th class="text-center">Protected</th>
              </tr>
            </thead>
            <tbody>
              <!-- Zoho FSM -->
              <ng-container *ngIf="previewData.preview.zohoFsm">
                <tr *ngIf="previewData.preview.zohoFsm.drones">
                  <td>Zoho FSM - Drones</td>
                  <td class="text-center">{{ previewData.preview.zohoFsm.drones.total }}</td>
                  <td class="text-center text-danger fw-bold">{{ previewData.preview.zohoFsm.drones.toDelete }}</td>
                  <td class="text-center"><span class="badge bg-secondary">{{ previewData.preview.zohoFsm.drones.protected }}</span></td>
                </tr>
                <tr *ngIf="previewData.preview.zohoFsm.serviceAppointments">
                  <td>Zoho FSM - Service Appointments</td>
                  <td class="text-center">{{ previewData.preview.zohoFsm.serviceAppointments.total }}</td>
                  <td class="text-center text-danger fw-bold">{{ previewData.preview.zohoFsm.serviceAppointments.toDelete }}</td>
                  <td class="text-center"><span class="badge bg-secondary">{{ previewData.preview.zohoFsm.serviceAppointments.protected }}</span></td>
                </tr>
                <tr *ngIf="previewData.preview.zohoFsm.workOrders">
                  <td>Zoho FSM - Work Orders</td>
                  <td class="text-center">{{ previewData.preview.zohoFsm.workOrders.total }}</td>
                  <td class="text-center text-danger fw-bold">{{ previewData.preview.zohoFsm.workOrders.toDelete }}</td>
                  <td class="text-center"><span class="badge bg-secondary">{{ previewData.preview.zohoFsm.workOrders.protected }}</span></td>
                </tr>
                <tr *ngIf="previewData.preview.zohoFsm.organizations">
                  <td>Zoho FSM - Organizations</td>
                  <td class="text-center">{{ previewData.preview.zohoFsm.organizations.total }}</td>
                  <td class="text-center text-danger fw-bold">{{ previewData.preview.zohoFsm.organizations.toDelete }}</td>
                  <td class="text-center"><span class="badge bg-secondary">{{ previewData.preview.zohoFsm.organizations.protected }}</span></td>
                </tr>
                <tr *ngIf="previewData.preview.zohoFsm.contacts">
                  <td>Zoho FSM - Contacts</td>
                  <td class="text-center">{{ previewData.preview.zohoFsm.contacts.total }}</td>
                  <td class="text-center text-danger fw-bold">{{ previewData.preview.zohoFsm.contacts.toDelete }}</td>
                  <td class="text-center"><span class="badge bg-secondary">{{ previewData.preview.zohoFsm.contacts.protected }}</span></td>
                </tr>
              </ng-container>
              <!-- Zoho Desk -->
              <ng-container *ngIf="previewData.preview.zohoDesk">
                <tr *ngIf="previewData.preview.zohoDesk.supportTickets">
                  <td>Zoho Desk - Support Tickets</td>
                  <td class="text-center">{{ previewData.preview.zohoDesk.supportTickets.total }}</td>
                  <td class="text-center text-danger fw-bold">{{ previewData.preview.zohoDesk.supportTickets.toDelete }}</td>
                  <td class="text-center"><span class="badge bg-secondary">{{ previewData.preview.zohoDesk.supportTickets.protected }}</span></td>
                </tr>
                <tr *ngIf="previewData.preview.zohoDesk.organizations">
                  <td>Zoho Desk - Organizations</td>
                  <td class="text-center">{{ previewData.preview.zohoDesk.organizations.total }}</td>
                  <td class="text-center text-danger fw-bold">{{ previewData.preview.zohoDesk.organizations.toDelete }}</td>
                  <td class="text-center"><span class="badge bg-secondary">{{ previewData.preview.zohoDesk.organizations.protected }}</span></td>
                </tr>
                <tr *ngIf="previewData.preview.zohoDesk.contacts">
                  <td>Zoho Desk - Contacts</td>
                  <td class="text-center">{{ previewData.preview.zohoDesk.contacts.total }}</td>
                  <td class="text-center text-danger fw-bold">{{ previewData.preview.zohoDesk.contacts.toDelete }}</td>
                  <td class="text-center"><span class="badge bg-secondary">{{ previewData.preview.zohoDesk.contacts.protected }}</span></td>
                </tr>
                <tr *ngIf="previewData.preview.zohoDesk.drones">
                  <td>Zoho Desk - Drones</td>
                  <td class="text-center">{{ previewData.preview.zohoDesk.drones.total }}</td>
                  <td class="text-center text-danger fw-bold">{{ previewData.preview.zohoDesk.drones.toDelete }}</td>
                  <td class="text-center"><span class="badge bg-secondary">{{ previewData.preview.zohoDesk.drones.protected }}</span></td>
                </tr>
              </ng-container>
              <!-- Firebase -->
              <ng-container *ngIf="previewData.preview.firebase">
                <tr *ngIf="previewData.preview.firebase.users">
                  <td>Firebase - Users</td>
                  <td class="text-center">{{ previewData.preview.firebase.users.total }}</td>
                  <td class="text-center text-danger fw-bold">{{ previewData.preview.firebase.users.toDelete }}</td>
                  <td class="text-center"><span class="badge bg-secondary">{{ previewData.preview.firebase.users.protected }}</span></td>
                </tr>
              </ng-container>
            </tbody>
          </table>
        </div>

        <!-- Protected Records -->
        <div *ngIf="previewData.protectedRecords.drones?.length || previewData.protectedRecords.profiles?.length" class="mb-4">
          <h6 class="mb-3">Protected Records (will be skipped)</h6>
          <div class="row">
            <div *ngIf="previewData.protectedRecords.drones?.length" class="col-md-6 mb-3">
              <div class="card">
                <div class="card-body">
                  <h6 class="card-subtitle mb-2 text-muted">Protected Drones</h6>
                  <ul class="mb-0 ps-3">
                    <li *ngFor="let drone of previewData.protectedRecords.drones">{{ drone }}</li>
                  </ul>
                </div>
              </div>
            </div>
            <div *ngIf="previewData.protectedRecords.profiles?.length" class="col-md-6 mb-3">
              <div class="card">
                <div class="card-body">
                  <h6 class="card-subtitle mb-2 text-muted">Protected Profiles</h6>
                  <ul class="mb-0 ps-3">
                    <li *ngFor="let profile of previewData.protectedRecords.profiles">{{ profile }}</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Estimated Duration -->
        <div class="alert alert-info d-flex align-items-center mb-4">
          <i class="bi bi-clock me-3 fs-4"></i>
          <div>
            <strong>Estimated Duration:</strong> {{ formatDuration(previewData.estimatedDurationSeconds) }}
          </div>
        </div>

        <!-- Warnings -->
        <div *ngIf="previewData.warnings.length > 0" class="alert alert-warning mb-0">
          <h6 class="alert-heading"><i class="bi bi-exclamation-triangle me-2"></i>Warnings</h6>
          <ul class="mb-0">
            <li *ngFor="let warning of previewData.warnings">{{ warning }}</li>
          </ul>
        </div>
      </div>
    </div>

    <!-- Action Buttons -->
    <div class="d-flex justify-content-between">
      <button type="button" class="btn btn-outline-secondary" (click)="goToSelection()">
        <i class="bi bi-arrow-left me-2"></i>Back to Selection
      </button>
      <button type="button" class="btn btn-danger" (click)="goToConfirmation()">
        Proceed to Confirmation
        <i class="bi bi-arrow-right ms-2"></i>
      </button>
    </div>
  </div>

  <!-- STEP 3: Confirmation Dialog -->
  <div *ngIf="wizardState === 'confirm'">
    <div class="card border-danger mb-4">
      <div class="card-header bg-danger text-white">
        <h5 class="mb-0"><i class="bi bi-exclamation-triangle me-2"></i>Final Confirmation Required</h5>
      </div>
      <div class="card-body">
        <div class="alert alert-danger mb-4">
          <strong>Warning:</strong> This action is irreversible. The following data will be permanently deleted:
        </div>

        <!-- Selected Categories Summary -->
        <div class="mb-4">
          <h6>Categories to be reset:</h6>
          <ul class="list-group">
            <li *ngFor="let category of getSelectedCategories()" class="list-group-item list-group-item-danger">
              <i class="bi bi-trash me-2"></i>{{ category }}
            </li>
          </ul>
        </div>

        <!-- Confirmation Input -->
        <div class="mb-4">
          <label for="confirmPhrase" class="form-label">
            Type <strong class="text-danger">{{ requiredPhrase }}</strong> to confirm:
          </label>
          <input type="text" class="form-control form-control-lg font-monospace"
                 id="confirmPhrase"
                 [(ngModel)]="confirmationPhrase"
                 [class.is-valid]="getConfirmationState() === 'valid'"
                 [class.is-invalid]="getConfirmationState() === 'invalid'"
                 placeholder="Type the confirmation phrase"
                 autocomplete="off">
          <div *ngIf="getConfirmationState() === 'invalid'" class="invalid-feedback">
            Phrase does not match. Please type exactly: {{ requiredPhrase }}
          </div>
          <div *ngIf="getConfirmationState() === 'valid'" class="valid-feedback">
            Confirmation phrase matches. You may now execute the reset.
          </div>
        </div>

        <!-- Backup Reminder -->
        <div class="alert alert-secondary d-flex align-items-center">
          <i class="bi bi-database me-3 fs-4"></i>
          <div>
            <strong>Reminder:</strong> Ensure you have a database backup before proceeding. Deleted data cannot be recovered.
          </div>
        </div>
      </div>
    </div>

    <!-- Action Buttons -->
    <div class="d-flex justify-content-between">
      <button type="button" class="btn btn-outline-secondary" (click)="goBackToPreview()">
        <i class="bi bi-arrow-left me-2"></i>Back to Preview
      </button>
      <button type="button" class="btn btn-danger btn-lg"
              [disabled]="!isConfirmationValid()"
              (click)="executeReset()">
        <i class="bi bi-trash me-2"></i>Execute Reset
      </button>
    </div>
  </div>

  <!-- STEP 4: Progress Display -->
  <div *ngIf="wizardState === 'progress'">
    <div class="card mb-4">
      <div class="card-header">
        <h5 class="mb-0">
          <span class="spinner-border spinner-border-sm me-2" role="status"></span>
          Reset in Progress
        </h5>
      </div>
      <div class="card-body">
        <!-- Overall Progress -->
        <div class="mb-4">
          <div class="d-flex justify-content-between mb-2">
            <span>Overall Progress</span>
            <span>{{ getOverallProgress() }}%</span>
          </div>
          <div class="progress" style="height: 24px;">
            <div class="progress-bar progress-bar-striped progress-bar-animated"
                 role="progressbar"
                 [style.width.%]="getOverallProgress()">
            </div>
          </div>
        </div>

        <!-- Per-Category Progress -->
        <div class="mb-4">
          <h6 class="mb-3">Category Progress</h6>
          <div *ngFor="let entry of getProgressEntries()" class="mb-3">
            <div class="d-flex justify-content-between mb-1">
              <span>{{ formatCategoryKey(entry.key) }}</span>
              <span>
                <span *ngIf="entry.value.status === 'pending'" class="badge bg-secondary">Pending</span>
                <span *ngIf="entry.value.status === 'processing'" class="badge bg-info">Processing</span>
                <span *ngIf="entry.value.status === 'completed'" class="badge bg-success">Completed</span>
                <span *ngIf="entry.value.status === 'error'" class="badge bg-danger">Error</span>
                <span class="ms-2">{{ entry.value.current }}/{{ entry.value.total }}</span>
              </span>
            </div>
            <div class="progress" style="height: 8px;">
              <div class="progress-bar"
                   [class.progress-bar-striped]="entry.value.status === 'processing'"
                   [class.progress-bar-animated]="entry.value.status === 'processing'"
                   [class.bg-success]="entry.value.status === 'completed'"
                   [class.bg-danger]="entry.value.status === 'error'"
                   role="progressbar"
                   [style.width.%]="entry.value.total > 0 ? (entry.value.current / entry.value.total) * 100 : 0">
              </div>
            </div>
          </div>
        </div>

        <!-- Error Count -->
        <div *ngIf="errorCount > 0" class="alert alert-warning d-flex align-items-center">
          <i class="bi bi-exclamation-triangle me-2"></i>
          <span>{{ errorCount }} error(s) encountered during processing</span>
        </div>

        <!-- Non-dismissible warning -->
        <div class="alert alert-info">
          <i class="bi bi-info-circle me-2"></i>
          Please do not close this window or navigate away during the reset operation.
        </div>
      </div>
    </div>
  </div>

  <!-- STEP 5: Results Display -->
  <div *ngIf="wizardState === 'results'">
    <!-- Success Banner -->
    <div *ngIf="isSuccess() && !executeError" class="alert alert-success d-flex align-items-center mb-4" role="alert">
      <i class="bi bi-check-circle-fill me-3 fs-4"></i>
      <div>
        <strong>Reset Completed Successfully</strong>
        <p class="mb-0">All selected integration data has been reset.</p>
      </div>
    </div>

    <!-- Partial Failure Banner -->
    <div *ngIf="isPartialFailure() && !executeError" class="alert alert-warning d-flex align-items-center mb-4" role="alert">
      <i class="bi bi-exclamation-triangle-fill me-3 fs-4"></i>
      <div>
        <strong>Reset Completed with Errors</strong>
        <p class="mb-0">Some records could not be deleted. See details below.</p>
      </div>
    </div>

    <!-- Error Banner -->
    <div *ngIf="executeError" class="alert alert-danger d-flex align-items-center mb-4" role="alert">
      <i class="bi bi-x-circle-fill me-3 fs-4"></i>
      <div>
        <strong>Reset Failed</strong>
        <p class="mb-0">{{ executeError }}</p>
      </div>
    </div>

    <!-- Results Details -->
    <div *ngIf="executeResults" class="card mb-4">
      <div class="card-header">
        <h5 class="mb-0">Results Summary</h5>
      </div>
      <div class="card-body">
        <!-- Summary Stats -->
        <div class="row mb-4">
          <div class="col-md-4">
            <div class="card text-bg-success text-center">
              <div class="card-body p-3">
                <h2 class="mb-0">{{ getTotalDeleted() }}</h2>
                <small class="text-uppercase">Records Deleted</small>
              </div>
            </div>
          </div>
          <div class="col-md-4">
            <div class="card text-bg-secondary text-center">
              <div class="card-body p-3">
                <h2 class="mb-0">{{ getTotalSkipped() }}</h2>
                <small class="text-uppercase">Records Skipped</small>
              </div>
            </div>
          </div>
          <div class="col-md-4">
            <div class="card text-center" [class.text-bg-danger]="getTotalErrors() > 0" [class.text-bg-success]="getTotalErrors() === 0">
              <div class="card-body p-3">
                <h2 class="mb-0">{{ getTotalErrors() }}</h2>
                <small class="text-uppercase">Errors</small>
              </div>
            </div>
          </div>
        </div>

        <!-- Detailed Breakdown -->
        <h6 class="mb-3">Detailed Breakdown</h6>
        <div class="table-responsive mb-4">
          <table class="table table-bordered">
            <thead>
              <tr>
                <th>Category</th>
                <th class="text-center">Deleted</th>
                <th class="text-center">Skipped</th>
                <th class="text-center">Errors</th>
              </tr>
            </thead>
            <tbody>
              <!-- Zoho FSM Results -->
              <ng-container *ngIf="executeResults.results.zohoFsm">
                <tr *ngIf="executeResults.results.zohoFsm.drones">
                  <td>Zoho FSM - Drones</td>
                  <td class="text-center text-success">{{ executeResults.results.zohoFsm.drones.deleted }}</td>
                  <td class="text-center">{{ executeResults.results.zohoFsm.drones.skipped }}</td>
                  <td class="text-center" [class.text-danger]="executeResults.results.zohoFsm.drones.errors > 0">{{ executeResults.results.zohoFsm.drones.errors }}</td>
                </tr>
                <tr *ngIf="executeResults.results.zohoFsm.serviceAppointments">
                  <td>Zoho FSM - Service Appointments</td>
                  <td class="text-center text-success">{{ executeResults.results.zohoFsm.serviceAppointments.deleted }}</td>
                  <td class="text-center">{{ executeResults.results.zohoFsm.serviceAppointments.skipped }}</td>
                  <td class="text-center" [class.text-danger]="executeResults.results.zohoFsm.serviceAppointments.errors > 0">{{ executeResults.results.zohoFsm.serviceAppointments.errors }}</td>
                </tr>
                <tr *ngIf="executeResults.results.zohoFsm.workOrders">
                  <td>Zoho FSM - Work Orders</td>
                  <td class="text-center text-success">{{ executeResults.results.zohoFsm.workOrders.deleted }}</td>
                  <td class="text-center">{{ executeResults.results.zohoFsm.workOrders.skipped }}</td>
                  <td class="text-center" [class.text-danger]="executeResults.results.zohoFsm.workOrders.errors > 0">{{ executeResults.results.zohoFsm.workOrders.errors }}</td>
                </tr>
                <tr *ngIf="executeResults.results.zohoFsm.organizations">
                  <td>Zoho FSM - Organizations</td>
                  <td class="text-center text-success">{{ executeResults.results.zohoFsm.organizations.deleted }}</td>
                  <td class="text-center">{{ executeResults.results.zohoFsm.organizations.skipped }}</td>
                  <td class="text-center" [class.text-danger]="executeResults.results.zohoFsm.organizations.errors > 0">{{ executeResults.results.zohoFsm.organizations.errors }}</td>
                </tr>
                <tr *ngIf="executeResults.results.zohoFsm.contacts">
                  <td>Zoho FSM - Contacts</td>
                  <td class="text-center text-success">{{ executeResults.results.zohoFsm.contacts.deleted }}</td>
                  <td class="text-center">{{ executeResults.results.zohoFsm.contacts.skipped }}</td>
                  <td class="text-center" [class.text-danger]="executeResults.results.zohoFsm.contacts.errors > 0">{{ executeResults.results.zohoFsm.contacts.errors }}</td>
                </tr>
              </ng-container>
              <!-- Zoho Desk Results -->
              <ng-container *ngIf="executeResults.results.zohoDesk">
                <tr *ngIf="executeResults.results.zohoDesk.supportTickets">
                  <td>Zoho Desk - Support Tickets</td>
                  <td class="text-center text-success">{{ executeResults.results.zohoDesk.supportTickets.deleted }}</td>
                  <td class="text-center">{{ executeResults.results.zohoDesk.supportTickets.skipped }}</td>
                  <td class="text-center" [class.text-danger]="executeResults.results.zohoDesk.supportTickets.errors > 0">{{ executeResults.results.zohoDesk.supportTickets.errors }}</td>
                </tr>
                <tr *ngIf="executeResults.results.zohoDesk.organizations">
                  <td>Zoho Desk - Organizations</td>
                  <td class="text-center text-success">{{ executeResults.results.zohoDesk.organizations.deleted }}</td>
                  <td class="text-center">{{ executeResults.results.zohoDesk.organizations.skipped }}</td>
                  <td class="text-center" [class.text-danger]="executeResults.results.zohoDesk.organizations.errors > 0">{{ executeResults.results.zohoDesk.organizations.errors }}</td>
                </tr>
                <tr *ngIf="executeResults.results.zohoDesk.contacts">
                  <td>Zoho Desk - Contacts</td>
                  <td class="text-center text-success">{{ executeResults.results.zohoDesk.contacts.deleted }}</td>
                  <td class="text-center">{{ executeResults.results.zohoDesk.contacts.skipped }}</td>
                  <td class="text-center" [class.text-danger]="executeResults.results.zohoDesk.contacts.errors > 0">{{ executeResults.results.zohoDesk.contacts.errors }}</td>
                </tr>
                <tr *ngIf="executeResults.results.zohoDesk.drones">
                  <td>Zoho Desk - Drones</td>
                  <td class="text-center text-success">{{ executeResults.results.zohoDesk.drones.deleted }}</td>
                  <td class="text-center">{{ executeResults.results.zohoDesk.drones.skipped }}</td>
                  <td class="text-center" [class.text-danger]="executeResults.results.zohoDesk.drones.errors > 0">{{ executeResults.results.zohoDesk.drones.errors }}</td>
                </tr>
              </ng-container>
              <!-- Firebase Results -->
              <ng-container *ngIf="executeResults.results.firebase">
                <tr *ngIf="executeResults.results.firebase.users">
                  <td>Firebase - Users</td>
                  <td class="text-center text-success">{{ executeResults.results.firebase.users.deleted }}</td>
                  <td class="text-center">{{ executeResults.results.firebase.users.skipped }}</td>
                  <td class="text-center" [class.text-danger]="executeResults.results.firebase.users.errors > 0">{{ executeResults.results.firebase.users.errors }}</td>
                </tr>
              </ng-container>
            </tbody>
          </table>
        </div>

        <!-- Error Log -->
        <div *ngIf="executeResults.errors && executeResults.errors.length > 0">
          <h6 class="mb-3">Error Log</h6>
          <div class="accordion" id="errorLogAccordion">
            <div class="accordion-item">
              <h2 class="accordion-header">
                <button class="accordion-button collapsed" type="button" data-bs-toggle="collapse" data-bs-target="#errorLogCollapse">
                  <i class="bi bi-exclamation-triangle me-2"></i>
                  {{ executeResults.errors.length }} Error(s)
                </button>
              </h2>
              <div id="errorLogCollapse" class="accordion-collapse collapse" data-bs-parent="#errorLogAccordion">
                <div class="accordion-body">
                  <div class="table-responsive">
                    <table class="table table-sm">
                      <thead>
                        <tr>
                          <th>Category</th>
                          <th>Record ID</th>
                          <th>Error</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr *ngFor="let error of executeResults.errors">
                          <td>{{ error.category }}</td>
                          <td class="font-monospace small">{{ error.id }}</td>
                          <td class="text-danger">{{ error.error }}</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Audit Event Link -->
        <div *ngIf="executeResults.auditEventId" class="alert alert-info mt-4">
          <i class="bi bi-journal-text me-2"></i>
          Audit Event ID: <code>{{ executeResults.auditEventId }}</code>
        </div>

        <!-- Duration -->
        <div class="text-muted mt-3">
          <i class="bi bi-clock me-1"></i>
          Completed in {{ formatDuration(executeResults.durationSeconds) }}
        </div>
      </div>
    </div>

    <!-- Action Buttons -->
    <div class="d-flex justify-content-between">
      <button type="button" class="btn btn-outline-primary" (click)="viewHistory()">
        <i class="bi bi-clock-history me-2"></i>View History
      </button>
      <button type="button" class="btn btn-primary" (click)="returnToDashboard()">
        <i class="bi bi-house me-2"></i>Return to Dashboard
      </button>
    </div>
  </div>
</div>
